import { toApiError } from '@/shared/api';

/** Gateway oder gar keine Antwort: der Ausgang ist offen, nicht „falsch“. */
const NO_ANSWER = new Set([0, 502, 503, 504]);

export interface AuthFailure {
  title: string;
  text: string;
  /** Ohne Antwort: offen, ob der Server den Schritt ausgeführt hat. */
  unknown?: boolean;
}

const noAnswer: AuthFailure = {
  title: 'Keine Antwort vom Server.',
  text: 'Der Server war nicht erreichbar. Versuch es gleich noch einmal.',
  unknown: true,
};
const tooMany: AuthFailure = {
  title: 'Zu viele Versuche.',
  text: 'Warte einen Moment und versuch es dann noch einmal.',
};
const serverError = (status: number): AuthFailure => ({
  title: 'Anmeldung gerade nicht möglich.',
  text: `Der Server meldet einen Fehler (Status ${status}). Versuch es gleich noch einmal.`,
});

/**
 * Satz zu einer gescheiterten Anmeldung. Gesperrt, unbestätigt und falsche Daten klingen gleich:
 * ein eigener Satz verriete, dass es das Konto gibt. Der Hinweis auf die Sperre steht deshalb bei
 * jedem abgelehnten Versuch.
 */
export async function loginFailure(error: unknown): Promise<AuthFailure> {
  const problem = await toApiError(error);
  if (NO_ANSWER.has(problem.status)) return noAnswer;
  if (problem.status === 429) return tooMany;
  if (problem.status === 401 || problem.status === 400)
    return {
      title: 'Das hat nicht geklappt.',
      text: 'Prüf Unternehmenskürzel, E-Mail und Passwort (Groß- und Kleinschreibung zählt). Nach fünf Fehlversuchen ist die Anmeldung 15 Minuten gesperrt.',
    };
  return serverError(problem.status);
}

const CODE_TEXT = {
  code: 'Entweder stimmt der Code nicht, oder die Anmeldung ist abgelaufen (nach 5 Minuten oder 5 Fehlversuchen). Nimm den aktuellen Code; klappt es wieder nicht, geh zurück und melde dich neu an.',
  setup:
    'Entweder stimmt der Code nicht, oder die Einrichtung ist abgelaufen (nach 5 Minuten oder 5 Fehlversuchen). Nimm den aktuellen Code aus der App; klappt es wieder nicht, geh zurück und melde dich neu an.',
};

/**
 * Satz zur zweiten Stufe. Der Server meldet beim Code jeden Fehlschlag gleich (falsch, abgelaufen,
 * aufgebraucht), also sagt der Satz beides, statt „falsch“ zu behaupten. Das Passwort ist hier
 * schon geprüft; mehr Genauigkeit verriete kein Konto, bräuchte aber eigene Codes vom Server.
 */
export async function twoFactorFailure(
  error: unknown,
  step: keyof typeof CODE_TEXT,
): Promise<AuthFailure> {
  const problem = await toApiError(error);
  if (NO_ANSWER.has(problem.status)) return noAnswer;
  if (problem.status === 429) return tooMany;
  if (problem.title === 'Auth.InvalidTwoFactorChallenge')
    return {
      title: 'Die Anmeldung ist abgelaufen.',
      text:
        step === 'setup'
          ? 'Codes und Eintrag in der App gelten nicht mehr; nach der neuen Anmeldung richtest du beides neu ein.'
          : 'Geh zurück und melde dich neu an.',
    };
  if (problem.status === 401) return { title: 'Code nicht angenommen.', text: CODE_TEXT[step] };
  return serverError(problem.status);
}
